<template>
  <!--
    POST-UAT-UX / PUX-A (doc 35/01 §1) -- the page of the book a tutor reply
    cites, full viewport, opened from that citation's `[^n]` marker.

    - Fit to the screen first; zoom in / out / back to fit; drag (pointer or
      one finger) to move a zoomed page; pinch with two fingers; the wheel
      zooms at the pointer; ONE click / tap on the page toggles fit <-> 2.5x
      at that point (QA-31, P41 -- it used to take a double click); keys:
      Escape, + / =, -, 0, arrows.
    - The signed URL is used only as this `<img>`'s source: it is never text,
      never a link, never logged; the image is fetched only now. Nothing here
      sends a Tutor request or touches credits.
    - A page that will not load asks ONCE for a refresh (`expired` -> the
      existing bounded `media-expired` refetch, which re-signs the URL), loads
      the new URL if one arrives, and otherwise says the page is not
      available. The reply and its Sources list are not touched.
    - Modal: moved to <body> (no transformed / clipped ancestor in either
      chat host), `aria-modal`, Tab kept inside, focus on «بستن» first, the
      page behind does not scroll; the caller returns focus to the marker.
  -->
  <div
    class="citation-viewer"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    data-testid="ai-tutor-citation-viewer"
    :data-viewer-status="status"
    @keydown="onKeydown"
  >
    <header class="citation-viewer__bar">
      <div class="citation-viewer__title">
        <strong :id="titleId">{{
          $t('aiTutorSetup.chat.sources.viewer.title', { n: displayN })
        }}</strong>
        <span v-if="sourceLabel" class="citation-viewer__source">{{
          sourceLabel
        }}</span>
      </div>
      <div class="citation-viewer__tools">
        <button
          type="button"
          class="citation-viewer__button"
          data-testid="ai-tutor-citation-zoom-out"
          :disabled="status !== 'ready' || zoom <= minZoom"
          :aria-label="$t('aiTutorSetup.chat.sources.viewer.zoomOut')"
          :title="$t('aiTutorSetup.chat.sources.viewer.zoomOut')"
          @click="zoomBy(1 / zoomStep)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 12h14" />
          </svg>
        </button>
        <span
          class="citation-viewer__level"
          data-testid="ai-tutor-citation-zoom-level"
          aria-live="polite"
          >{{ levelText }}</span
        >
        <button
          type="button"
          class="citation-viewer__button"
          data-testid="ai-tutor-citation-zoom-in"
          :disabled="status !== 'ready' || zoom >= maxZoom"
          :aria-label="$t('aiTutorSetup.chat.sources.viewer.zoomIn')"
          :title="$t('aiTutorSetup.chat.sources.viewer.zoomIn')"
          @click="zoomBy(zoomStep)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 12h14M12 5v14" />
          </svg>
        </button>
        <button
          type="button"
          class="citation-viewer__button"
          data-testid="ai-tutor-citation-fit"
          :disabled="status !== 'ready'"
          :aria-label="$t('aiTutorSetup.chat.sources.viewer.fit')"
          :title="$t('aiTutorSetup.chat.sources.viewer.fit')"
          @click="reset"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M4 9V5a1 1 0 0 1 1-1h4M15 4h4a1 1 0 0 1 1 1v4M20 15v4a1 1 0 0 1-1 1h-4M9 20H5a1 1 0 0 1-1-1v-4"
            />
          </svg>
        </button>
        <button
          ref="close"
          type="button"
          class="citation-viewer__button citation-viewer__button--close"
          data-testid="ai-tutor-citation-close"
          :aria-label="$t('aiTutorSetup.chat.sources.viewer.close')"
          :title="$t('aiTutorSetup.chat.sources.viewer.close')"
          @click="close"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </div>
    </header>

    <div
      ref="stage"
      class="citation-viewer__stage"
      :class="{
        'citation-viewer__stage--zoomed': zoom > minZoom,
        'citation-viewer__stage--dragging': dragging,
      }"
      data-testid="ai-tutor-citation-stage"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @wheel.prevent="onWheel"
      @click="onStageClick"
    >
      <img
        v-if="currentUrl && status !== 'unavailable'"
        ref="image"
        :key="currentUrl"
        class="citation-viewer__image"
        :class="{ 'citation-viewer__image--hidden': status !== 'ready' }"
        :src="currentUrl"
        :alt="$t('aiTutorSetup.chat.sources.viewer.imageAlt', { n: displayN })"
        :style="imageStyle"
        draggable="false"
        referrerpolicy="no-referrer"
        data-testid="ai-tutor-citation-image"
        @load="onLoad"
        @error="onError"
      />
      <p
        v-if="status === 'loading' || status === 'refreshing'"
        class="citation-viewer__state"
        dir="auto"
        role="status"
        data-testid="ai-tutor-citation-loading"
      >
        {{ $t('aiTutorSetup.chat.sources.viewer.loading') }}
      </p>
      <div
        v-else-if="status === 'unavailable'"
        class="citation-viewer__state citation-viewer__state--unavailable"
        dir="auto"
        role="alert"
        data-testid="ai-tutor-citation-unavailable"
      >
        <strong>{{
          $t('aiTutorSetup.chat.sources.viewer.unavailable')
        }}</strong>
        <span>{{
          $t('aiTutorSetup.chat.sources.viewer.unavailableHint')
        }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// Zoom is relative to "fit": 1 = the whole page on screen.
const MIN_ZOOM = 1
const MAX_ZOOM = 6
const ZOOM_STEP = 1.5
const DOUBLE_ZOOM = 2.5
const KEY_PAN = 48
// A refreshed (re-signed) URL is waited for this long before the page is
// declared not available.
export const REFRESH_WAIT_MS = 8000
// Movement below this, in total from the press, is a tap, not a drag.
const TAP_SLOP = 6
// QA-31: a second click this soon and this near the last toggle is the rest
// of a double tap, whatever its `detail` says (a tap's synthesized click may
// keep `detail` at 1), not a second toggle.
const DOUBLE_TAP_MS = 350
const DOUBLE_TAP_SLOP = 24

let instances = 0

export default {
  name: 'CitationPageViewer',
  props: {
    // The citation's number (`[^n]`).
    n: {
      type: Number,
      required: true,
    },
    // The source's signed page image, read from the CURRENT turn by the
    // caller (a refetch replaces it); '' when the turn no longer has one.
    imageUrl: {
      type: String,
      default: '',
    },
    // The source line (title · page · item), as the Sources list shows it.
    sourceLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    instances += 1
    return {
      titleId: `ai-tutor-citation-viewer-title-${instances}`,
      // 'loading' | 'ready' | 'refreshing' | 'unavailable'
      status: this.imageUrl ? 'loading' : 'unavailable',
      currentUrl: this.imageUrl || '',
      refreshed: false,
      natural: { width: 0, height: 0 },
      stageSize: { width: 0, height: 0 },
      zoom: MIN_ZOOM,
      tx: 0,
      ty: 0,
      dragging: false,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      zoomStep: ZOOM_STEP,
    }
  },
  computed: {
    locale() {
      return (this.$i18n && this.$i18n.locale) || 'fa'
    },
    displayN() {
      return this.formatNumber(this.n)
    },
    levelText() {
      return `${this.formatNumber(Math.round(this.zoom * 100))}%`
    },
    fitScale() {
      const { width, height } = this.natural
      const stage = this.stageSize
      if (!width || !height || !stage.width || !stage.height) return 1
      return Math.min(stage.width / width, stage.height / height)
    },
    scale() {
      return this.fitScale * this.zoom
    },
    imageStyle() {
      const { width, height } = this.natural
      if (!width || !height) return {}
      return {
        width: `${width}px`,
        height: `${height}px`,
        transform:
          `translate(calc(-50% + ${this.tx}px), calc(-50% + ${this.ty}px)) ` +
          `scale(${this.scale})`,
      }
    },
  },
  watch: {
    // A refetch brought a (re-signed) URL: load it. Only while the page is
    // not showing -- a new signature of a page that is on screen changes
    // nothing.
    imageUrl(next) {
      if (this.status === 'ready' || this.status === 'loading') {
        if (!next) return
        if (this.status === 'loading') this.currentUrl = next
        return
      }
      if (this.status !== 'refreshing') return
      this.clearRefreshTimer()
      if (next && next !== this.currentUrl) {
        this.currentUrl = next
        this.status = 'loading'
      } else {
        this.status = 'unavailable'
      }
    },
  },
  created() {
    this.pointers = new Map()
    this.gesture = null
    this.refreshTimer = null
    this.moved = false
    this.pressedAt = null
    this.lastToggle = null
    this.previousOverflow = null
  },
  mounted() {
    // Out of the chat's own layout, onto the page.
    if (this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el)
    }
    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('resize', this.measure)
    this.measure()
    this.$nextTick(() => {
      if (this.$refs.close) this.$refs.close.focus()
    })
  },
  beforeDestroy() {
    this.clearRefreshTimer()
    window.removeEventListener('resize', this.measure)
    document.body.style.overflow = this.previousOverflow || ''
    if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el)
  },
  methods: {
    formatNumber(value) {
      try {
        return Number(value).toLocaleString(
          this.locale === 'fa' ? 'fa-IR' : 'en-US'
        )
      } catch (err) {
        return String(value)
      }
    },
    close() {
      this.$emit('close')
    },
    measure() {
      const stage = this.$refs.stage
      if (!stage) return
      const rect = stage.getBoundingClientRect()
      this.stageSize = { width: rect.width, height: rect.height }
      this.clampPan()
    },
    onLoad(event) {
      const image = event && event.target
      this.natural = {
        width: (image && image.naturalWidth) || 0,
        height: (image && image.naturalHeight) || 0,
      }
      this.measure()
      this.reset()
      this.status = 'ready'
    },
    // One refresh, then "not available". The URL itself is never reported.
    onError() {
      if (this.refreshed) {
        this.status = 'unavailable'
        return
      }
      this.refreshed = true
      this.status = 'refreshing'
      this.$emit('expired')
      this.clearRefreshTimer()
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null
        if (this.status === 'refreshing') this.status = 'unavailable'
      }, REFRESH_WAIT_MS)
    },
    clearRefreshTimer() {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    // ---- geometry (stage-centre coordinates, left-to-right) ---------------
    clampPan() {
      const { width, height } = this.natural
      const halfX = Math.max(0, (width * this.scale - this.stageSize.width) / 2)
      const halfY = Math.max(
        0,
        (height * this.scale - this.stageSize.height) / 2
      )
      this.tx = Math.min(halfX, Math.max(-halfX, this.tx))
      this.ty = Math.min(halfY, Math.max(-halfY, this.ty))
    },
    // Zoom to `next` keeping the page point under (px, py) in place.
    zoomAt(next, px = 0, py = 0) {
      if (this.status !== 'ready') return
      const target = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next))
      const ratio = target / this.zoom
      this.tx = px - (px - this.tx) * ratio
      this.ty = py - (py - this.ty) * ratio
      this.zoom = target
      if (target === MIN_ZOOM) {
        this.tx = 0
        this.ty = 0
      }
      this.clampPan()
    },
    zoomBy(factor) {
      this.zoomAt(this.zoom * factor)
    },
    reset() {
      this.zoom = MIN_ZOOM
      this.tx = 0
      this.ty = 0
    },
    panBy(dx, dy) {
      if (this.zoom <= MIN_ZOOM) return
      this.tx += dx
      this.ty += dy
      this.clampPan()
    },
    stagePoint(clientX, clientY) {
      const stage = this.$refs.stage
      const rect = stage
        ? stage.getBoundingClientRect()
        : { left: 0, top: 0, width: 0, height: 0 }
      return {
        x: clientX - rect.left - rect.width / 2,
        y: clientY - rect.top - rect.height / 2,
      }
    },
    // ---- pointer: drag, pinch ---------------------------------------------
    onPointerDown(event) {
      if (this.status !== 'ready') return
      this.pointers.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      })
      if (this.pointers.size === 1) {
        this.moved = false
        this.pressedAt = { x: event.clientX, y: event.clientY }
      }
      const stage = this.$refs.stage
      if (stage && typeof stage.setPointerCapture === 'function') {
        try {
          stage.setPointerCapture(event.pointerId)
        } catch (err) {
          // not capturable (synthetic event) -- dragging still works
        }
      }
      this.gesture = this.pointers.size >= 2 ? this.pinchStart() : null
    },
    onPointerMove(event) {
      const last = this.pointers.get(event.pointerId)
      if (!last) return
      const now = { x: event.clientX, y: event.clientY }
      this.pointers.set(event.pointerId, now)
      if (this.pointers.size >= 2 && this.gesture) {
        this.moved = true
        this.pinchMove()
        return
      }
      const dx = now.x - last.x
      const dy = now.y - last.y
      if (Math.abs(dx) + Math.abs(dy) > 0 && this.zoom > MIN_ZOOM) {
        this.dragging = true
      }
      // QA-31: from the press, not per event -- a slow drag is many tiny
      // moves, and it is still a drag.
      const from = this.pressedAt || last
      if (
        !this.moved &&
        Math.hypot(now.x - from.x, now.y - from.y) > TAP_SLOP
      ) {
        this.moved = true
      }
      this.panBy(dx, dy)
    },
    onPointerUp(event) {
      this.pointers.delete(event.pointerId)
      if (this.pointers.size < 2) this.gesture = null
      if (this.pointers.size === 0) this.dragging = false
    },
    pinchPair() {
      const [a, b] = Array.from(this.pointers.values())
      return {
        distance: Math.hypot(b.x - a.x, b.y - a.y) || 1,
        mid: this.stagePoint((a.x + b.x) / 2, (a.y + b.y) / 2),
      }
    },
    pinchStart() {
      const pair = this.pinchPair()
      return { distance: pair.distance, zoom: this.zoom, mid: pair.mid }
    },
    pinchMove() {
      const pair = this.pinchPair()
      const start = this.gesture
      // The midpoint's own movement pans; the spread zooms around it.
      this.tx += pair.mid.x - start.mid.x
      this.ty += pair.mid.y - start.mid.y
      start.mid = pair.mid
      this.zoomAt(
        (start.zoom * pair.distance) / start.distance,
        pair.mid.x,
        pair.mid.y
      )
    },
    onWheel(event) {
      if (this.status !== 'ready') return
      const point = this.stagePoint(event.clientX, event.clientY)
      const factor = Math.exp(-(event.deltaY || 0) * 0.0015)
      this.zoomAt(this.zoom * factor, point.x, point.y)
    },
    // Fit <-> 2.5x, keeping the page point under the pointer in place.
    toggleZoomAt(event) {
      if (this.status !== 'ready') return
      const point = this.stagePoint(event.clientX, event.clientY)
      this.zoomAt(
        this.zoom > MIN_ZOOM ? MIN_ZOOM : DOUBLE_ZOOM,
        point.x,
        point.y
      )
    },
    // A tap on the dark area around the page closes; a tap on the page, a
    // drag or a pinch never does. QA-31 (P41): ONE still click or tap on the
    // page zooms in at that point (the `zoom-in` cursor promised it, and it
    // took a double click); on a zoomed page it returns to fit. The second
    // click of a double click (`detail` 2) is the same gesture, not another
    // toggle -- so a double click still lands zoomed in, never back at fit.
    onStageClick(event) {
      if (this.moved) {
        this.moved = false
        return
      }
      // By position, not by target: the stage captures the pointer on press,
      // so a still mouse click on the page is retargeted to the stage
      // (focused review P2).
      const image = this.$refs.image
      const rect =
        image && this.status === 'ready' ? image.getBoundingClientRect() : null
      const onImage =
        !!image &&
        (event.target === image ||
          (!!rect &&
            rect.width > 0 &&
            rect.height > 0 &&
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom))
      if (!onImage) {
        if (this.status !== 'loading' && this.status !== 'refreshing') {
          this.close()
        }
        return
      }
      if (event.detail > 1) return
      const now = Date.now()
      const last = this.lastToggle
      if (
        last &&
        now - last.at < DOUBLE_TAP_MS &&
        Math.hypot(event.clientX - last.x, event.clientY - last.y) <=
          DOUBLE_TAP_SLOP
      ) {
        return
      }
      this.lastToggle = { at: now, x: event.clientX, y: event.clientY }
      this.toggleZoomAt(event)
    },
    // ---- keyboard -----------------------------------------------------------
    onKeydown(event) {
      const key = event.key
      if (key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        this.close()
        return
      }
      if (key === 'Tab') {
        this.trapTab(event)
        return
      }
      if (this.status !== 'ready') return
      const pans = {
        ArrowLeft: [KEY_PAN, 0],
        ArrowRight: [-KEY_PAN, 0],
        ArrowUp: [0, KEY_PAN],
        ArrowDown: [0, -KEY_PAN],
      }
      if (key === '+' || key === '=') this.zoomBy(ZOOM_STEP)
      else if (key === '-' || key === '_') this.zoomBy(1 / ZOOM_STEP)
      else if (key === '0') this.reset()
      else if (pans[key]) this.panBy(pans[key][0], pans[key][1])
      else return
      event.preventDefault()
    },
    trapTab(event) {
      const focusable = Array.from(
        this.$el.querySelectorAll('button:not([disabled])')
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (event.shiftKey && (active === first || !this.$el.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (
        !event.shiftKey &&
        (active === last || !this.$el.contains(active))
      ) {
        event.preventDefault()
        first.focus()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Above every app layer (the bottom navigation 40, the Student dialogs 160).
.citation-viewer {
  position: fixed;
  inset: 0;
  z-index: 170;
  display: flex;
  flex-direction: column;
  background: rgba(7, 12, 33, 0.92);
  color: #fff;
  font-family: $borna-font-family;
  overscroll-behavior: contain;
}

.citation-viewer__bar {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 64px;
  padding: 10px max(16px, env(safe-area-inset-right)) 10px
    max(16px, env(safe-area-inset-left));
  background: rgba(7, 12, 33, 0.72);
}

.citation-viewer__title {
  display: grid;
  min-width: 0;
  gap: 1px;

  strong {
    font-size: 14px;
    font-weight: $borna-fw-extrabold;
    line-height: 1.6;
  }
}

.citation-viewer__source {
  overflow: hidden;
  color: rgba(255, 255, 255, 0.72);
  font-size: 11px;
  line-height: 1.7;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.citation-viewer__tools {
  display: flex;
  flex: none;
  align-items: center;
  gap: 6px;
}

.citation-viewer__button {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.18);
  }

  &:focus-visible {
    outline: 3px solid $borna-indigo-300;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &--close {
    margin-inline-start: 6px;
    background: rgba(255, 255, 255, 0.16);
  }
}

.citation-viewer__level {
  min-width: 46px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 12px;
  font-weight: $borna-fw-bold;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

// The page itself is laid out left-to-right in both locales: only the chrome
// follows the page direction.
.citation-viewer__stage {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  direction: ltr;
  cursor: zoom-out;
  touch-action: none;
  user-select: none;

  &--zoomed {
    cursor: grab;
  }

  &--dragging {
    cursor: grabbing;
  }
}

.citation-viewer__image {
  position: absolute;
  top: 50%;
  left: 50%;
  max-width: none;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);
  cursor: inherit;
  transform-origin: center center;
  -webkit-user-drag: none;

  &--hidden {
    visibility: hidden;
  }

  .citation-viewer__stage:not(.citation-viewer__stage--zoomed) & {
    cursor: zoom-in;
  }
}

.citation-viewer__state {
  position: absolute;
  top: 50%;
  left: 50%;
  display: grid;
  gap: 4px;
  width: min(360px, calc(100% - 32px));
  margin: 0;
  padding: 16px 18px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 13px;
  line-height: 1.8;
  text-align: center;
  transform: translate(-50%, -50%);

  span {
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .citation-viewer__bar {
    gap: 8px;
    min-height: 60px;
    padding-block: 8px;
  }

  .citation-viewer__tools {
    gap: 4px;
  }

  .citation-viewer__level {
    display: none;
  }

  .citation-viewer__button--close {
    margin-inline-start: 2px;
  }
}
</style>
